import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Ventana propia del panel (nunca window.confirm ni alert: bloquean la página y no se ven
 * como KNOC360). Esc o clic afuera la cierran; el foco entra y vuelve adonde estaba.
 *
 * `cerrarAfuera={false}`: un clic fuera no la cierra. Se usa en la ventana del código de
 * alta, que se ve una sola vez: un clic de más no se puede llevar el código.
 */
export function Modal({
  titulo,
  alCerrar,
  children,
  pie,
  cerrarConEsc = true,
  cerrarAfuera = true,
}: {
  titulo: string;
  alCerrar: () => void;
  children: ReactNode;
  pie?: ReactNode;
  cerrarConEsc?: boolean;
  cerrarAfuera?: boolean;
}) {
  const id = useId();
  const caja = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const antes = document.activeElement as HTMLElement | null;
    caja.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    // con una ventana encima de otra, Esc cierra solo la de arriba
    const tecla = (e: KeyboardEvent) => {
      const fondos = document.querySelectorAll('.modal-fondo');
      if (cerrarConEsc && e.key === 'Escape' && fondos[fondos.length - 1] === caja.current?.parentElement) alCerrar();
    };
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('keydown', tecla);
      antes?.focus?.();
    };
  }, [alCerrar, cerrarConEsc]);
  return createPortal(
    <div className="modal-fondo" onMouseDown={(e) => cerrarAfuera && e.target === e.currentTarget && alCerrar()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={id} ref={caja}>
        <h2 id={id}>{titulo}</h2>
        <div className="modal-cuerpo">{children}</div>
        {pie && <div className="modal-pie">{pie}</div>}
      </div>
    </div>,
    document.body,
  );
}
